import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookingSection, PlacesLeft } from "@/components/BookingForm";
import { GuideBlock } from "@/components/GuideBlock";
import { MUMS_BABIES } from "@/data/mumsBabies";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const CONTACT_LINK = "/contact?topic=Mums%20%26%20Babies%20Circle";

const BookButton = ({ label, testid }) => (
  <a
    href="#book"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Hero = ({ mb }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="mb-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/mums-babies-hero.jpg" alt="Mothers and babies settled together on cushions and mats in a warm room" className="h-full w-full object-cover scale-105 brightness-[0.82] sepia-[0.12] contrast-[1.02]" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/40 to-ink/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-ink/25" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-96 bg-gradient-to-t from-ink/60 to-transparent sm:hidden" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-6 max-w-xl text-xs tracking-[0.3em] uppercase text-cream/85"
          data-testid="mb-hero-eyebrow"
        >
          {mb.hero.eyebrow}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight"
            data-testid="mb-hero-title"
          >
            {mb.name}
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-7 max-w-2xl font-serif italic text-2xl leading-snug text-sand"
          data-testid="mb-hero-sub"
        >
          {mb.hero.sub}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-cream"
          data-testid="mb-hero-intro"
        >
          {mb.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="mb-hero-facts"
        >
          {mb.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span className={f.includes("€") ? "font-medium text-cream" : ""}>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <BookButton label={mb.hero.primaryCta} testid="mb-hero-book" />
          <a href="#next-circle" data-testid="mb-hero-dates"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {mb.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`mb-faq-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-6 text-left" data-testid={`mb-faq-toggle-${index}`}>
        <span className="font-serif text-xl sm:text-2xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-7 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MumsBabiesPage() {
  const { lang } = useLang();
  const mb = MUMS_BABIES[lang] || MUMS_BABIES.en;
  useSeo({ ...mb.seo, image: `/images/mums-babies-hero.jpg` });

  return (
    <main data-testid="mums-babies-page">
      <Hero mb={mb} />

      <section className="bg-cream py-28 lg:py-40" data-testid="mb-space">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink text-center">{mb.space.title}</h2>
            <p className="mt-10 text-base leading-relaxed text-soft drop-cap">{mb.space.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.space.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.space.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.space.p4}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 border border-clay/30 bg-parchment/60 p-10 lg:p-12 text-center" data-testid="mb-space-pull">
              <p className="font-serif text-2xl sm:text-3xl leading-snug text-clay">{mb.space.pull}</p>
              <p className="mt-5 text-base leading-relaxed text-soft">{mb.space.pullBody}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <BookingSection
        offer="mums-babies"
        banner={
          <div id="next-circle" className="mb-10 border-b border-ink/10 pb-8" data-testid="mb-booking-band">
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-5">{mb.next.heading}</p>
            <p className="font-serif text-3xl sm:text-4xl text-ink" data-testid="mb-next-dates">{mb.next.datesBig}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="mb-next-chips">
              {mb.next.chips.map((d) => (
                <span key={d} className="rounded-full border border-clay/30 px-4 py-1.5 text-xs tracking-wide text-clay">{d}</span>
              ))}
            </div>
            <div className="mt-6 text-sm leading-relaxed text-soft">
              <p>{mb.next.time} · {mb.next.venue}</p>
              <p>{mb.next.line}</p>
              <p>{mb.next.capacity}</p>
              <p className="mt-2"><span className="inline-block rounded-full border border-sage/40 bg-sage/10 px-4 py-1.5 text-xs tracking-wide text-sage" data-testid="mb-next-spots"><PlacesLeft offer="mums-babies" dates={[{ value: "2026-11-06", short: "" }]} /></span></p>
              <p>{mb.next.ages}</p>
              <p className="mt-2 font-serif italic text-ink/70" data-testid="mb-next-closed">{mb.next.closedNote}</p>
            </div>
          </div>
        }
        dates={[
          { value: "2026-11-06", label: lang === "nl" ? "Zes vrijdagen — 6 november tot 11 december 2026" : "Six Fridays — 6 November to 11 December 2026" },
        ]}
        tickets={null}
        priceLabel="€190"
      />


      <section className="bg-parchment py-28 lg:py-40" data-testid="mb-baby">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{mb.baby.title}</h2>
            <p className="mt-10 font-serif italic text-2xl sm:text-3xl leading-snug text-clay">{mb.baby.lead}</p>
            <p className="mt-10 text-base leading-relaxed text-soft">{mb.baby.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.baby.p2}</p>
            <p className="mt-12 font-serif italic text-xl sm:text-2xl leading-snug text-ink/80">{mb.baby.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-24" data-testid="mb-testimonials">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mb.testimonials.heading}</h2>
            <div className="mt-12 grid gap-10 sm:grid-cols-2 text-left">
              {mb.testimonials.items.map((t, i) => (
                <blockquote key={i} data-testid={`mb-testimonial-${i}`}>
                  <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>"</p>
                  <p className="mt-2 font-serif italic text-lg leading-relaxed text-ink/85">{t.text}</p>
                  <footer className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">— {t.name}</footer>
                </blockquote>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="mb-why6">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{mb.why6.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{mb.why6.lead}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{mb.why6.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mb.why6.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mb.why6.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="mb-weeks">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-12">{mb.weeks.heading}</h2>
          </Reveal>
          <div>
            {mb.weeks.items.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.05}>
                <div className="border-t border-ink/10 py-8 grid gap-2 sm:grid-cols-12 sm:gap-8" data-testid={`mb-week-${i}`}>
                  <p className="sm:col-span-3 text-xs tracking-[0.3em] uppercase text-clay pt-2">{w.n}</p>
                  <div className="sm:col-span-9">
                    <p className="font-serif text-2xl sm:text-3xl leading-snug text-ink">{w.title}</p>
                    <p className="mt-3 text-base leading-relaxed text-soft">{w.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="mb-rhythm">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img loading="lazy" src="/images/postpartum.jpg" alt="Tea and candlelight — the unhurried atmosphere of the weekly circle" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-4">{mb.rhythm.heading}</p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mb.rhythm.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{mb.rhythm.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mb.rhythm.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mb.rhythm.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{mb.rhythm.p4}</p>
            <p className="mt-7 font-serif italic text-xl leading-snug text-clay">{mb.rhythm.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="mb-suitability">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mb.suitability.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{mb.suitability.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.suitability.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.suitability.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{mb.suitability.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="mb-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="mb-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{mb.irida.heading}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{mb.irida.body}</p>
            <Link to="/about" data-testid="mb-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {mb.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-24" data-testid="mb-crosslink">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <div className="border border-ink/10 bg-parchment/60 p-10 lg:p-14 text-center">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{mb.crossLink.title}</h2>
              <p className="mt-5 text-sm leading-relaxed text-soft max-w-xl mx-auto">{mb.crossLink.body}</p>
              <Link to="/offerings/one-to-one-mothers" data-testid="mb-crosslink-cta"
                className="group mt-7 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
                {mb.crossLink.cta}
                <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <GuideBlock ids={["who-is-caring-for-you"]} bg="bg-cream" testid="mb-guide" />

      <section className="bg-parchment py-24 lg:py-32" data-testid="mb-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-8">{mb.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {mb.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24 lg:py-36 grain relative" data-testid="mb-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream">{mb.final.title}</h2>
            <p className="mt-10 text-xs tracking-[0.3em] uppercase text-sand/80">{mb.final.heading}</p>
            <p className="mt-4 font-serif text-2xl text-cream" data-testid="mb-final-dates">{mb.final.dates}</p>
            <p className="mt-2 text-sm text-cream/70">{mb.final.line1}</p>
            <p className="mt-1 text-sm text-cream/70">{mb.final.line2}</p>
            <p className="mt-1 text-sm text-sand/70 italic font-serif">{mb.final.note}</p>
            <div className="mt-10">
              <BookButton label={mb.final.cta} testid="mb-final-book" />
            </div>
            <Link to={CONTACT_LINK} data-testid="mb-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {mb.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
